import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Searcher from 'components/DesignSystem/Searcher';
import Snackbar from 'components/DesignSystem/Snackbar';
import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useCultures from 'hooks/Meteorology/Agro/CultureIndex/Location/useCultures';
import useStages from 'hooks/Meteorology/Agro/CultureIndex/Location/useStages';
import useGroupOptions from 'hooks/Meteorology/Agro/CultureIndex/Location/useGroupOptions';
import useLocationOptions from 'hooks/Meteorology/Agro/CultureIndex/Location/useLocationOptions';
import useForecastDate from 'hooks/Meteorology/Agro/CultureIndex/Location/useForecastDate';
import useForecastPeriod from 'hooks/Meteorology/Agro/CultureIndex/Location/useForecastPeriod';
import useParams from 'hooks/Meteorology/Agro/CultureIndex/Location/useParams';
import { store } from 'redux/store';
import {
  reset,
  selectMeteorologyAgroCultureIndexLocationTriggerRequest,
} from 'redux/reducers/meteorologyAgroCultureIndexLocationReducer';
import { cultureIndexLocationApi } from 'services/api/Meteorology/Agro/CultureIndex/Location';

import './index.scss';

type SheetSection = 'culture' | 'stage' | 'group' | 'location' | 'forecast' | 'period' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('culture');
  const [groupSearch, setGroupSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');

  const { cultures, selectedCulture, setCulture } = useCultures();
  const { stages, selectedStage, setStage } = useStages();
  const { availableGroups, selectedGroupId, setGroup } = useGroupOptions();
  const { availableLocations, selectedLocationId, setLocation } = useLocationOptions();
  const { availableDates, forecastDate, setForecastDate } = useForecastDate();
  const {
    availablePeriod, changePeriod, periodStart, periodEnd,
  } = useForecastPeriod();
  const params = useParams();
  const triggerRequest = useSelector(selectMeteorologyAgroCultureIndexLocationTriggerRequest);

  useEffect(() => {
    if (open) setOpenSection('culture');
  }, [open]);

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const confirm = () => {
    if (triggerRequest) triggerRequest();
    dismiss();
  };

  const clear = () => {
    store.dispatch(reset());
    store.dispatch(cultureIndexLocationApi.util.resetApiState());
    dismiss();
  };

  const pattern = (search: string) => new RegExp(search.split('').join('.*'), 'i');
  const groupPattern = pattern(groupSearch);
  const locationPattern = pattern(locationSearch);

  const selectedGroupName = availableGroups.find((g) => g.id === selectedGroupId)?.name;
  const selectedLocationName = availableLocations.find((l) => l.id === selectedLocationId)?.name;
  const periodLabel = periodStart && periodEnd
    ? `${periodStart.toLocaleDateString('pt-BR')} - ${periodEnd.toLocaleDateString('pt-BR')}`
    : undefined;
  const forecastLabel = forecastDate ? forecastDate.toLocaleDateString('pt-BR') : undefined;

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="agro-ci-location-control-panel-sheet"
    >
      <div className="agro-ci-location-sheet-body">
        <Section
          label="Cultura"
          valueText={selectedCulture?.label}
          open={openSection === 'culture'}
          onToggle={toggleSection('culture')}
        >
          {cultures.length === 0 && (
            <Snackbar className="mt-2">Nenhuma cultura disponível.</Snackbar>
          )}
          {cultures.map((c) => (
            <OptionRow
              key={c.name}
              label={c.label}
              selected={c.name === selectedCulture?.name}
              onSelect={() => setCulture(c)}
            />
          ))}
        </Section>

        <Section
          label="Estágio fenológico"
          valueText={selectedStage?.label}
          open={openSection === 'stage'}
          onToggle={toggleSection('stage')}
        >
          {!selectedCulture && (
            <Snackbar className="mt-2">Selecione uma cultura primeiro.</Snackbar>
          )}
          {selectedCulture && stages.length === 0 && (
            <Snackbar className="mt-2">Nenhum estágio disponível.</Snackbar>
          )}
          {selectedCulture && stages.map((s) => (
            <OptionRow
              key={s.name}
              label={s.label}
              selected={s.name === selectedStage?.name}
              onSelect={() => setStage(s)}
            />
          ))}
        </Section>

        <Section
          label="Grupo"
          valueText={selectedGroupName}
          open={openSection === 'group'}
          onToggle={toggleSection('group')}
        >
          <Searcher value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} />
          {availableGroups.length === 0 && (
            <Snackbar className="mt-2">Nenhum grupo disponível.</Snackbar>
          )}
          {availableGroups.filter((g) => groupPattern.test(g.name)).map((g) => (
            <OptionRow
              key={g.id}
              label={g.name}
              selected={g.id === selectedGroupId}
              onSelect={() => setGroup(g.id)}
            />
          ))}
        </Section>

        <Section
          label="Local"
          valueText={selectedLocationName}
          open={openSection === 'location'}
          onToggle={toggleSection('location')}
        >
          <Searcher value={locationSearch} onChange={(e) => setLocationSearch(e.target.value)} />
          {availableLocations.length === 0 && (
            <Snackbar className="mt-2">
              {selectedGroupId === undefined
                ? 'Selecione um grupo primeiro.'
                : 'Nenhum local disponível para o grupo selecionado.'}
            </Snackbar>
          )}
          {availableLocations.filter((l) => locationPattern.test(l.name)).map((l) => (
            <OptionRow
              key={l.id}
              label={l.name}
              selected={l.id === selectedLocationId}
              onSelect={() => setLocation(l.id)}
            />
          ))}
        </Section>

        <Section
          label="Data de previsão"
          valueText={forecastLabel}
          open={openSection === 'forecast'}
          onToggle={toggleSection('forecast')}
        >
          <DatePicker
            inline
            selected={forecastDate}
            onChange={setForecastDate}
            includeDates={availableDates}
          />
        </Section>

        <Section
          label="Período de previsão"
          valueText={periodLabel}
          open={openSection === 'period'}
          onToggle={toggleSection('period')}
        >
          <DatePicker
            inline
            selectsRange
            startDate={periodStart}
            endDate={periodEnd}
            onChange={changePeriod}
            includeDates={availablePeriod}
          />
        </Section>
      </div>

      <div className="agro-ci-location-sheet-footer">
        <SheetActionButton
          variant="confirm"
          disabled={!params || triggerRequest === undefined}
          onClick={confirm}
        >
          Realizar consulta
        </SheetActionButton>
        <SheetActionButton variant="clear" onClick={clear}>
          Limpar filtros
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
